import type { Result, AppError } from '../../error/types';
import { ok, err, tryCatch } from '../../error/functions';

/**
 * @group Functions
 * @summary <blockquote>Detects the Next.js runtime environment (nodejs, edge, or undefined).</blockquote>
 * 
 * @returns Result containing the detected Next.js runtime or undefined if not in Next.js context
 * 
 * @example
 * ```typescript
 * import { getNextjsRuntime } from '@shared/utils';
 * 
 * const runtimeResult = getNextjsRuntime();
 * runtimeResult.match({
 *   ok: (runtime) => {
 *     if (runtime === 'edge') {
 *       // Use Edge-compatible code
 *     }
 *   },
 *   err: (error) => console.error(`Detection failed: ${error.errorMessage}`)
 * });
 * ```
 */
export function getNextjsRuntime(): Result<'nodejs' | 'edge' | undefined, AppError> {
  return tryCatch(() => {
    // NEXT_RUNTIME is a Next.js internal runtime variable (set automatically by Next.js)
    // It's not a user-configured environment variable, so we access it directly
    const nextRuntime = typeof process !== 'undefined' ? process.env?.NEXT_RUNTIME : undefined;
    
    if (nextRuntime === 'nodejs' || nextRuntime === 'edge') {
      return nextRuntime;
    }
    
    // Return undefined if NEXT_RUNTIME is not set (e.g., in tests or non-Next.js contexts)
    return undefined;
  }).match({
    ok: (value) => ok(value),
    err: (error) => err({
      errorCode: 'NEXTJS_RUNTIME_DETECTION_FAILED',
      errorMessage: `Failed to detect Next.js runtime: ${error instanceof Error ? error.message : 'Unknown error'}`
    })
  });
}

